// react
import React, { Suspense, useId, useMemo, useRef, useState } from 'react';
import type { ReactElement, RefObject } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

// material ui
import {
  alpha,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';

// api types
import type { TimeSeriesDataPoint } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';

// shared components
import useHighchartsThemeSync from '@/hooks/useHighchartsThemeSync';
import { formatNumber } from '@/utils/formatters';

import Highcharts, { merge } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';

interface PortfolioAnalysisServiceCardProps {
  disabled?: boolean;
  name?: string;
  label?: string;
  path?: string;
  value?: number;
  unit?: string;
  icon: ReactElement;
  color: string;
  summedData?: TimeSeriesDataPoint[];
}

interface SparklineWrapperProps {
  dataPoints: TimeSeriesDataPoint[];
  unit?: string;
  color: string;
}

interface SparklineDialogProps {
  open: boolean;
  onClose: () => void;
  dataPoints?: TimeSeriesDataPoint[];
  unit?: string;
  color: string;
  label?: string;
}

interface AreaGradientProps {
  color: string;
  id: string;
}

interface GroupedDataPoint {
  timestamp: string;
  value: number;
}

const PortfolioAnalysisServiceCard = (
  props: PortfolioAnalysisServiceCardProps,
) => {
  const {
    disabled = false,
    name,
    label = name,
    path,
    value,
    unit,
    icon,
    color,
    summedData,
  } = props;
  const { t } = useTranslation('portfolio-analysis_dashboard');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const canOpenDialog = Array.isArray(summedData) && summedData.length > 0;

  const content = (
    <CardContent sx={{ pb: 0 }}>
      <Box
        flexDirection={'row'}
        display={'flex'}
        justifyContent={'space-between'}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {disabled ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Typography color="textSecondary">
                {t('cards.unavailable')}
              </Typography>
            </Box>
          ) : (
            <Box display={'flex'} flexDirection={'column'}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  color="textPrimary"
                  fontWeight={'bold'}
                >
                  {formatNumber(value, 0)} {unit}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {summedData && (
        <SparklineWrapper dataPoints={summedData} unit={unit} color={color} />
      )}
    </CardContent>
  );

  return (
    <Suspense fallback={<Skeleton />}>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardHeader
          title={label}
          sx={{ pb: 0 }}
          avatar={
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.1),
                borderRadius: 4,
                width: 48,
                height: 48,
              }}
            >
              {React.cloneElement(icon, {
                sx: { color: color, fontSize: 32 },
              })}
            </Avatar>
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {canOpenDialog ? (
                <Tooltip title={t('actions.showChart')}>
                  <IconButton
                    size="small"
                    aria-label={t('actions.showChart')}
                    onClick={() => setIsDialogOpen(true)}
                  >
                    <OpenInFullRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {path && !disabled ? (
                <Tooltip title={t('actions.openDetails')}>
                  <IconButton
                    size="small"
                    aria-label={t('actions.openDetails')}
                    component={Link}
                    to={`${path}`}
                  >
                    <LaunchRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          }
          slotProps={{
            title: {
              variant: 'h6',
              fontWeight: 'bold',
              color: 'textSecondary',
            },
          }}
        />
        {content}
      </Card>
      <SparklineDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        dataPoints={summedData}
        unit={unit}
        color={color}
        label={label}
      />
    </Suspense>
  );
};

const groupDataByWeek = (data: TimeSeriesDataPoint[]): GroupedDataPoint[] => {
  if (!data || data.length === 0) return [];
  const grouped = data.reduce<Record<string, GroupedDataPoint>>(
    (acc, { timestamp, value }) => {
      const weekStart = DateTime.fromISO(timestamp)
        .setZone('Europe/Copenhagen')
        .startOf('week');
      const weekKey = weekStart.toISODate();

      if (!weekKey) return acc;

      acc[weekKey] = {
        timestamp: weekStart.toISO() ?? timestamp,
        value: (acc[weekKey]?.value ?? 0) + value,
      };

      return acc;
    },
    {},
  );

  return Object.values(grouped).sort(
    (left, right) =>
      DateTime.fromISO(left.timestamp).toMillis() -
      DateTime.fromISO(right.timestamp).toMillis(),
  );
};

const formatWeekRangeLabel = (timestamp: string): string => {
  const start = DateTime.fromISO(timestamp)
    .setZone('Europe/Copenhagen')
    .setLocale('da-dk');
  const end = start.endOf('week');
  const weekNumber = start.weekNumber;

  return `Uge ${weekNumber}: ${start.toFormat('dd MMM yy')} - ${end.toFormat(
    'dd MMM yy',
  )}`;
};

const AreaGradient = ({ color, id }: AreaGradientProps) => (
  <defs>
    <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
      <stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient>
  </defs>
);

const SparklineWrapper = ({
  dataPoints,
  unit,
  color,
}: SparklineWrapperProps) => {
  const groupedData = useMemo(() => groupDataByWeek(dataPoints), [dataPoints]);
  const sparklineEntries =
    groupedData.length > 2 ? groupedData.slice(1, -1) : groupedData;

  const sparklineData = sparklineEntries
    .map((entry) => Number.parseInt(String(entry.value), 10))
    .filter((entry) => Number.isFinite(entry));
  const tooltipLabels = sparklineEntries.map((entry) =>
    formatWeekRangeLabel(entry.timestamp),
  );
  const gradientId = useId().replace(/:/g, '_');
  const settings = {
    valueFormatter: (value: number | null) =>
      `${formatNumber(value ?? undefined)} ${unit}`,
  };

  if (sparklineData.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        borderRadius: 1,
      }}
    >
      <SparkLineChart
        data={sparklineData}
        height={50}
        color={color}
        showTooltip
        showHighlight
        xAxis={{
          scaleType: 'band',
          data: tooltipLabels,
        }}
        area
        curve="monotoneX"
        {...settings}
        sx={{
          [`& .${areaElementClasses.root}`]: {
            fill: `url(#${gradientId})`,
            stroke: 'none',
          },
        }}
      >
        <AreaGradient color={color} id={gradientId} />
      </SparkLineChart>
    </Box>
  );
};

const SparklineDialog = ({
  open,
  onClose,
  dataPoints,
  unit,
  color,
  label,
}: SparklineDialogProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const initialTheme = useHighchartsThemeSync(
    chartRef as RefObject<{ chart: Highcharts.Chart }>,
    false,
  );
  const groupedData = useMemo(
    () => groupDataByWeek(dataPoints ?? []),
    [dataPoints],
  );

  const seriesData = useMemo(
    () =>
      groupedData
        .map((entry) => {
          const pointDate = DateTime.fromISO(entry.timestamp).setZone(
            'Europe/Copenhagen',
          );

          if (!pointDate.isValid) {
            return null;
          }

          return {
            x: pointDate.toUTC().toMillis(),
            y: entry.value,
            weekLabel: formatWeekRangeLabel(entry.timestamp),
          };
        })
        .filter(Boolean),
    [groupedData],
  );

  const chartOptions = useMemo(
    () =>
      merge(initialTheme, {
        chart: {
          height: 420,
          spacingLeft: 16,
          spacingRight: 16,
        },
        credits: { enabled: false },
        title: { text: '' },
        legend: { enabled: false },

        xAxis: merge(initialTheme?.xAxis ?? {}, {
          type: 'datetime',
          tickLength: 0,
        }),

        yAxis: [
          merge(initialTheme?.yAxis?.[0] ?? {}, {
            title: {
              text: unit ?? null,
            },
            min: 0,
          }),
        ],

        tooltip: merge(initialTheme?.tooltip ?? {}, {
          headerFormat: '{point.weekLabel}<br/>',
          pointFormatter: function (this: Highcharts.Point) {
            return `<span style="color:${color}">●</span> ${formatNumber(this.y)} ${unit ?? ''}`;
          },
        }),

        plotOptions: {
          series: {
            marker: {
              enabled: true,
              radius: 3,
            },
          },
        },

        series: [
          {
            type: 'line',
            name: label,
            data: seriesData,
            color,
          },
        ],
      }),
    [color, initialTheme, label, seriesData, unit],
  );

  if (!Array.isArray(dataPoints) || dataPoints.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{label}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ width: '100%' }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            ref={chartRef}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PortfolioAnalysisServiceCard;
